import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  Avatar,
} from '@ui5/webcomponents-react';

const GOOGLE_MAPS_KEY = 'AIzaSyDosZR1Zm3u7gfjWgKkuXhZcsvpEw4uQkc';

// Category icon/color mapping for markers
const CATEGORY_MARKERS = {
  1: { label: 'G', color: '#4CAF50' },  // Grocery & Shopping
  2: { label: 'R', color: '#FF9800' },  // Restaurants & Cafes
  3: { label: 'F', color: '#E91E63' },  // Family Entertainment
  4: { label: 'A', color: '#2196F3' },  // Adventure & Outdoors
  5: { label: 'N', color: '#9C27B0' },  // Nightlife & Bars
  6: { label: 'H', color: '#00BCD4' },  // Health & Wellness
  7: { label: 'S', color: '#795548' },  // Services & Essentials
};

let mapsLoaded = false;
let mapsLoadPromise = null;

function loadGoogleMaps() {
  if (mapsLoaded) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;

  mapsLoadPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      mapsLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=geocoding`;
    script.async = true;
    script.defer = true;
    script.onload = () => { mapsLoaded = true; resolve(); };
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

function LocationMap({ location, localTips, showLocalTips }) {
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState(false);

  const mapAddress = [location.address1, location.city, location.state, location.zipCode]
    .filter(Boolean).join(', ');

  useEffect(() => {
    if (!mapAddress) return;

    loadGoogleMaps()
      .then(() => {
        const geocoder = new window.google.maps.Geocoder();

        // Geocode the property address
        geocoder.geocode({ address: mapAddress }, (results, status) => {
          if (status !== 'OK' || !results[0]) {
            setMapError(true);
            return;
          }

          const propertyLatLng = results[0].geometry.location;
          const map = new window.google.maps.Map(mapRef.current, {
            center: propertyLatLng,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            styles: [
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] },
            ],
          });

          // Property marker
          new window.google.maps.Marker({
            position: propertyLatLng,
            map: map,
            title: 'Your Stay',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#0a6ed1',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 3,
            },
            zIndex: 10,
          });

          // Add info window for property
          const propertyInfo = new window.google.maps.InfoWindow({
            content: '<div style="font-weight:600;font-size:13px">Your Stay</div>',
          });
          // Show briefly then close
          const propertyMarker = map.getCenter();

          // Geocode and add local tips as markers
          if (showLocalTips && localTips && localTips.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(propertyLatLng);

            localTips.forEach((tip) => {
              const tipAddress = [tip.address, tip.city, tip.state, tip.zip]
                .filter(Boolean).join(', ');
              if (!tipAddress || tipAddress.trim() === '') return;

              const cat = CATEGORY_MARKERS[tip.category] || CATEGORY_MARKERS[7];

              geocoder.geocode({ address: tipAddress }, (tipResults, tipStatus) => {
                if (tipStatus !== 'OK' || !tipResults[0]) return;

                const tipLatLng = tipResults[0].geometry.location;
                bounds.extend(tipLatLng);

                const marker = new window.google.maps.Marker({
                  position: tipLatLng,
                  map: map,
                  title: tip.label,
                  label: {
                    text: cat.label,
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '600',
                  },
                  icon: {
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                    fillColor: cat.color,
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 1.5,
                    scale: 1.8,
                    anchor: new window.google.maps.Point(12, 22),
                    labelOrigin: new window.google.maps.Point(12, 9),
                  },
                });

                const infoWindow = new window.google.maps.InfoWindow({
                  content: `<div style="max-width:200px"><div style="font-weight:600;font-size:13px">${tip.label}</div>${tip.description ? '<div style="font-size:12px;color:#555;margin-top:2px">' + tip.description + '</div>' : ''}</div>`,
                });

                marker.addListener('click', () => {
                  infoWindow.open(map, marker);
                });

                // Fit map to include all markers
                map.fitBounds(bounds, 50);
                // Don't zoom in too far if only a few markers
                const listener = window.google.maps.event.addListener(map, 'idle', () => {
                  if (map.getZoom() > 15) map.setZoom(15);
                  window.google.maps.event.removeListener(listener);
                });
              });
            });
          }
        });
      })
      .catch(() => setMapError(true));
  }, [mapAddress, localTips, showLocalTips]);

  if (mapError) return null;

  return (
    <Card className="grid-card grid-card-full">
      <CardHeader
        avatar={<Avatar icon="map" colorScheme="Accent10" />}
        titleText="Location"
        subtitleText={[location.city, location.state].filter(Boolean).join(', ')}
      />
      <div className="map-container-js" ref={mapRef} />
    </Card>
  );
}

export default LocationMap;
